import { BISliderWithValueIndicator, useUpdateEffect } from '@pulsepointinc/bi-components';
import { type FC, useCallback, useEffect, useState } from 'react';
import { useFilters } from '../../../../../hooks/useFilters';
import type { Filter } from '../../../../../stores/filters';
import { useDebounce } from '../../hooks/useDebounce';
import ExpressionTypeSelector from '../CustomFilterComponents/ExpressionTypeSelector';
import { StyledDiv, StyledProgressBar } from './styles';

interface RangeSliderFilterProps {
  filterName: string;
  minValue: number;
  maxValue: number;
  isDual?: boolean;
  defaultValues?: number | number[];
  valueSuffix?: '$' | '%';
  onChange?: (value: string) => void;
  onExpressionTypeChange?: (value: string) => void;
}

const RangeSliderFilter: FC<RangeSliderFilterProps> = ({
  filterName,
  minValue,
  maxValue,
  valueSuffix,
  defaultValues,
  isDual = true,
  onChange,
  onExpressionTypeChange,
}) => {
  const getDefaults = () => {
    if (isDual && Array.isArray(defaultValues)) {
      return defaultValues;
    }
    if (!isDual && !!defaultValues) {
      return defaultValues;
    }

    if (!isDual && !defaultValues) {
      return maxValue;
    }
    if (isDual && !defaultValues) {
      return [minValue, maxValue];
    }
    return maxValue;
  };

  const [range, setRange] = useState<number | number[]>(getDefaults());
  const { filters } = useFilters();
  const filtersList = filters && (Object.values(filters) as Array<Filter>);

  const debouncedRange = useDebounce(range, 500);
  const sliderFilter = filtersList.find((filter) => filter.filterName === filterName);

  const valueChangeCallback = useCallback((value: number | number[]) => {
    setRange(value);
  }, []);

  useEffect(() => {
    const expression = sliderFilter?.expression;
    if (expression) {
      const numbers = expression.match(/\d+/g);
      if (numbers && numbers.length === 2) {
        const start = parseInt(numbers[0], 10);
        const end = parseInt(numbers[1], 10);
        setRange([start, end]);
      } else {
        setRange(Number(expression));
      }
    } else if (onChange) {
      if (Array.isArray(range)) {
        onChange(`[${range[0]},${range[1]}]`);
      } else {
        onChange(range.toString());
      }
    }
  }, []);

  useUpdateEffect(() => {
    if (onChange) {
      if (Array.isArray(debouncedRange)) {
        onChange(`[${debouncedRange[0]},${debouncedRange[1]}]`);
      }
      if (typeof debouncedRange === 'number') {
        onChange(debouncedRange.toString());
      }
    }
  }, [debouncedRange]);

  return (
    <StyledDiv>
      <ExpressionTypeSelector filterName={filterName} onExpressionTypeChange={onExpressionTypeChange} divider={false} />
      <StyledProgressBar>
        <BISliderWithValueIndicator
          min={minValue}
          max={maxValue}
          valueSuffix={valueSuffix}
          onChange={valueChangeCallback}
          initialValues={debouncedRange}
        />
      </StyledProgressBar>
    </StyledDiv>
  );
};

export default RangeSliderFilter;
